import { Icon } from 'blaze-ui/icons/Types'
import { AspectRatioKey } from 'components/NewFileModal/Types'
import { TextChannelKey } from 'types'
import { SectionProps } from './Sections/Types'
import {
  CreationAdTypes,
  CreationCategoryTypes,
  CreationEmailTypes,
  CreationMarketplaceTypes,
  CreationSocialMediaTypes,
  CreationVideoTypes,
} from 'components/FirstTimeExperienceModal/Types'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { GenerationType } from 'components/BlazeCommandLine/api/createDocGenerationRequest'

// sub categories
export type SocialMediaCategories = Exclude<SocialMediaTabs, 'popular'>

export type MarketplaceCategories = 'visual' | 'product_descriptions'

export type VideoCategories = 'instagram' | 'youtube' | 'tiktok' | 'facebook'

export type EmailCategories = 'newsletters' | 'lifecycle' | 'ecommerce' | 'sales'

export type OtherCategories = 'website' | 'sales' | 'customer_support' | 'recruiting'

export type PopularCategories = 'turn_into'

export type SubCategoryMap = {
  'For You': undefined
  Tools: undefined
  'Social media': SocialMediaCategories
  'Blog/SEO': undefined
  Marketplaces: MarketplaceCategories
  Video: VideoCategories
  Ads: undefined
  Emails: EmailCategories
  'Product Descriptions': undefined
  Website: undefined
  Sales: undefined
  'Customer Service': undefined
  Recruiting: undefined
  'Custom Size': undefined
  Brainstorm: undefined
  Popular: PopularCategories
  'Start Blank': undefined
  Other: OtherCategories
}

export type CategoryTypes =
  | 'For You'
  | 'Tools'
  | 'Social media'
  | 'Blog/SEO'
  | 'Marketplaces'
  | 'Video'
  | 'Ads'
  | 'Emails'
  | 'Product Descriptions'
  | 'Website'
  | 'Sales'
  | 'Customer Service'
  | 'Recruiting'
  | 'Custom Size'
  | 'Brainstorm'
  | 'Popular'
  | 'Start Blank'
  | 'Other'

export type SubCategoryTypes<C extends keyof SubCategoryMap> = SubCategoryMap[C]

export type AllSubCategories = SubCategoryMap[CategoryTypes]

export type SocialMediaTabs =
  | 'popular'
  | 'linkedIn'
  | 'instagram'
  | 'facebook'
  | 'twitter'
  | 'tiktok'
  | 'youtube'
  | 'pinterest'
  | 'whatsapp'

export type MarketplaceTabs = 'all' | 'visual' | 'product_descriptions' | 'shopify'

export type VideosTabs = 'popular' | 'instagram' | 'youtube' | 'tiktok'

export type EmailsSections = 'popular' | 'newsletters' | 'lifecycle' | 'ecommerce' | 'sales'

export type ToolsSections = 'ideas' | 'generate' | 'turn_into'

export type Category = {
  name: CategoryTypes
  title: string
  icon?: Icon
  component: React.FC<SectionProps>
}

// channels
export type ChannelKeyType = TextChannelKey | AspectRatioKey

export type SocialMediaChannels = Record<SocialMediaTabs, Array<ChannelKeyType>>

export type MarketplaceChannels = Record<MarketplaceTabs, Array<ChannelKeyType>>

export type MarketplaceChannelsV2 = Record<MarketplaceCategories, Array<ChannelKeyType>>

export type VideosChannels = Record<VideosTabs, Array<ChannelKeyType>>

export type EmailsChannels = Record<EmailsSections, Array<ChannelKeyType>>

export type EmailsChannelsV2 = Record<EmailCategories, Array<ChannelKeyType>>

export type ToolsChannels = Record<ToolsSections, Array<ChannelKeyType>>

export type OtherChannels = Record<OtherCategories, Array<ChannelKeyType>>

export type ForYouPages = 'recently_used' | 'getting_started' | 'power_up'

type CategorySubcategories = {
  social_media: CreationSocialMediaTypes
  email_newsletter: CreationEmailTypes
  ads: CreationAdTypes
  video: CreationVideoTypes
  marketplace: CreationMarketplaceTypes
  blogs_seo: never
  website: never
  other: never
}

export type CreationCategoryResult = {
  title: string
  channels: Array<ChannelKeyType>
}

export type CreationCategoryChannels = {
  [K in CreationCategoryTypes]: CategorySubcategories[K] extends never
    ? CreationCategoryResult
    : Record<CategorySubcategories[K], CreationCategoryResult>
}

export type WizardCardInfo = {
  title: string
  icon: React.ReactNode
  image: string
}

export const CREATE_NEW_EXCLUDED_CHANNELS = [
  ChannelType.InstagramPosts,
  ChannelType.Tweets,
  ChannelType.FacebookPost,
  ChannelType.LinkedInPosts,
  'instagram-square-post',
  'instagram-landscape-post',
  'landscape-post',
  'portrait-post',
  'square-post',
  ChannelType.InstagramCaptions,
] as const

export const VIRTUAL_MARKETER_CREATE_NEW_EXCLUDED_CHANNELS = [
  ...CREATE_NEW_EXCLUDED_CHANNELS,
  GenerationType.FacebookPlanning,
  GenerationType.TwitterPlanning,
  GenerationType.LinkedInPlanning,
  GenerationType.InstagramPlanning,
  GenerationType.SEOPlanning,
] as const

export type CreateNewExcludedChannels = (typeof CREATE_NEW_EXCLUDED_CHANNELS)[number]

export type V2Channels = Exclude<ChannelKeyType, CreateNewExcludedChannels>
